import Video, {
  Room,
  LocalVideoTrack,
  LocalAudioTrack,
  RemoteParticipant,
} from 'twilio-video';
import type {
  LocalTrack,
  RemoteTrack,
} from 'twilio-video';
import { attachRoomLogger, log } from '../../lib/twilio-diag/logger';
import type {
  VideoProvider,
  VideoRoomState,
  VideoTrack,
  VideoParticipant,
  VideoProviderConfig,
  MediaError,
  MediaKind,
  MediaRequest,
} from '../types';
import {
  isDismissalMessage,
  isSystemDenialMessage,
  kindsForRequest,
  queryCapturePermission,
  requestForKinds,
  whenCaptureGranted,
} from '../../lib/mediaPermissionState';

/**
 * Bounded timeout (ms) for local track acquisition via createLocalTracks.
 *
 * It measures the device, never the customer: it is armed only once the browser
 * permission is granted (see `whenCaptureGranted`). While the permission dialog
 * is open there is no deadline at all — a customer reading the question, opening
 * the device dropdown and pressing Allow used to lose the call at ten seconds.
 */
const CAPTURE_TIMEOUT_MS = 10_000;

type LocalTracks = Awaited<ReturnType<typeof Video.createLocalTracks>>;

/**
 * One outstanding `getUserMedia`, shared by every connect attempt waiting on it.
 *
 * `waiters` and `claimed` decide who owns the tracks when they finally arrive.
 * Without them the two rules of this file contradict each other: an attempt that
 * timed out stops the late tracks, and a "Try again" that joined the same
 * request would then be handed those stopped tracks and connect a room with a
 * dead microphone. The last waiter to give up is the one that cleans up, and
 * only if nobody claimed them.
 */
interface CaptureAttempt {
  key: string;
  promise: Promise<LocalTracks>;
  /** Attempts still waiting on this request. */
  waiters: number;
  /** True once an attempt has taken the tracks for its own room. */
  claimed: boolean;
  /** Resolves when this request's own getUserMedia has actually been issued. */
  started: Promise<void>;
}

type StateCallback = (state: VideoRoomState) => void;

export class TwilioVideoProvider implements VideoProvider {
  readonly name = 'twilio';

  private room: Room | null = null;
  private localTracks: LocalTrack[] = [];
  private diagDetach: (() => void) | null = null;
  private state: VideoRoomState = {
    isConnected: false,
    isConnecting: false,
    localParticipant: null,
    remoteParticipants: [],
    error: null,
    localNetworkQuality: 0,
    remoteNetworkQuality: 0,
  };
  private subscribers: Set<StateCallback> = new Set();
  /**
   * The capture request currently in flight, with the constraints it was made
   * under. A second `getUserMedia` while the first is unanswered is what turned
   * "Try again" into a loop: every press left another orphaned request and
   * another dialog behind it, and only a reload cleared them.
   */
  private capture: CaptureAttempt | null = null;
  /**
   * A device being switched on inside the room. A second press while the browser
   * dialog for it is still open joins this request instead of issuing another one
   * — the rule `capture` enforces for connect.
   */
  private acquiring: Partial<Record<MediaKind, Promise<void>>> = {};

  getState(): VideoRoomState {
    return { ...this.state };
  }

  subscribe(callback: StateCallback): () => void {
    this.subscribers.add(callback);
    return () => this.subscribers.delete(callback);
  }

  private notify(): void {
    const state = this.getState();
    this.subscribers.forEach(cb => cb(state));
  }

  private updateState(partial: Partial<VideoRoomState>): void {
    this.state = { ...this.state, ...partial };
    this.notify();
  }

  async connect(token: string, roomName: string, config?: VideoProviderConfig): Promise<void> {
    this.updateState({ isConnecting: true, error: null });

    const requestedKinds = TwilioVideoProvider.requestedKinds(config);
    const requested = requestForKinds(requestedKinds);

    try {
      // Create local tracks with optional device constraints
      // - If audio/video is explicitly false, disable that track type entirely
      // - If audioConstraints/videoConstraints is provided, pass as constraint object
      //   (enables { deviceId: { exact: "..." } } for device selection)
      // - Otherwise, pass true (use default device)
      const capture = this.acquireLocalTracks(config);
      const createTracksPromise = capture.promise;

      // Race createLocalTracks against a bounded timeout.
      // Uses a flag-based approach instead of a bare Promise.race to avoid
      // an unhandled-rejection from the losing branch.
      let captureResolved = false;
      let captureTimeoutId: ReturnType<typeof setTimeout> | undefined;
      let cancelArming: () => void = () => undefined;

      this.localTracks = await new Promise<LocalTracks>((resolve, reject) => {
        // Armed on the grant, not on the attempt: until the customer has answered
        // the browser, waiting is the correct behaviour and the only bound that
        // belongs here is their patience.
        const armTimeout = () => {
          if (captureResolved || captureTimeoutId !== undefined) return;
          captureTimeoutId = setTimeout(() => {
            if (captureResolved) return;
            captureResolved = true;
            const timeoutErr = Object.assign(
              new Error('Camera/microphone did not respond within the time limit.'),
              { name: 'CaptureTimeoutError' }
            );
            reject(timeoutErr);
          }, CAPTURE_TIMEOUT_MS);
        };
        // A request queued behind another one has not been issued yet, and a
        // device cannot be late before it has been asked: hold the clock until
        // this attempt's own getUserMedia is actually on the wire.
        cancelArming = whenCaptureGranted(requestedKinds, () => {
          void capture.started.then(armTimeout);
        });

        createTracksPromise.then(
          (tracks) => {
            capture.waiters -= 1;
            if (captureResolved) {
              // The timeout won and this attempt is over, but the browser still
              // handed us live devices. Releasing them is the whole difference
              // between a failed attempt and a camera light left on — unless
              // another attempt is still waiting on the same request, in which
              // case the tracks are its, not ours to stop.
              if (capture.waiters === 0 && !capture.claimed) {
                tracks.forEach((track) => {
                  if ('stop' in track && typeof track.stop === 'function') track.stop();
                });
              }
              return;
            }
            capture.claimed = true;
            captureResolved = true;
            clearTimeout(captureTimeoutId);
            cancelArming();
            resolve(tracks);
          },
          (err) => {
            capture.waiters -= 1;
            if (captureResolved) return;
            captureResolved = true;
            clearTimeout(captureTimeoutId);
            cancelArming();
            reject(err);
          }
        );
      });

      // Connect to room with Network Quality API enabled (verbosity: 1 = basic level)
      this.room = await Video.connect(token, {
        name: roomName,
        tracks: this.localTracks,
        networkQuality: {
          local: 1,
          remote: 1,
        },
      });

      // Attach diagnostic logger (no-op when toggle OFF; idempotent on remount)
      this.diagDetach = attachRoomLogger(this.room);

      // Build local participant
      const localParticipant = this.buildLocalParticipant();

      // Handle existing remote participants.
      // Track subscription is asynchronous: participant may be present at the
      // moment Video.connect() resolves but `pub.track` not yet populated.
      // We must wire trackSubscribed/trackUnsubscribed here as well — otherwise
      // when the buyer joins after the seller, the seller's video track is
      // subscribed later and state.remoteParticipants[*].videoTrack stays null.
      const remoteParticipants: VideoParticipant[] = [];
      this.room.participants.forEach(p => {
        remoteParticipants.push(this.buildRemoteParticipant(p));
        this.attachRemoteParticipantListeners(p);
      });

      this.updateState({
        isConnected: true,
        isConnecting: false,
        localParticipant,
        remoteParticipants,
      });

      // Listen for new participants
      this.room.on('participantConnected', (participant) => {
        const newParticipant = this.buildRemoteParticipant(participant);
        this.updateState({
          remoteParticipants: [...this.state.remoteParticipants, newParticipant],
        });
        this.attachRemoteParticipantListeners(participant);
      });

      this.room.on('participantDisconnected', (participant) => {
        this.updateState({
          remoteParticipants: this.state.remoteParticipants.filter(
            p => p.id !== participant.sid
          ),
          // Reset remote NQ when participant leaves
          remoteNetworkQuality: 0,
        });
      });

      this.room.on('disconnected', () => {
        this.diagDetach?.();
        this.diagDetach = null;
        this.updateState({
          // A capture error raised inside this room (a camera refused on the way to
          // video) belongs to the room. Left behind, it would make the lost room
          // look like a device problem, and the screen skips the reconnect for those.
          ...(this.state.error?.isLocalCapture ? { error: null } : {}),
          isConnected: false,
          localParticipant: null,
          remoteParticipants: [],
          localNetworkQuality: 0,
          remoteNetworkQuality: 0,
        });
      });

      // Network Quality API: listen for quality changes on local participant
      this.room.localParticipant.on('networkQualityLevelChanged', (level: number) => {
        this.updateState({ localNetworkQuality: level });
      });

    } catch (err) {
      // Stop any tracks that were partially acquired before the failure
      this.localTracks.forEach(track => {
        if ('stop' in track && typeof track.stop === 'function') {
          track.stop();
        }
      });
      this.localTracks = [];

      log('[twilio:room:failed]', {
        t: Date.now(),
        roomSid: null,
        roomName,
        localParticipantSid: null,
        reason: (err as Error)?.message ?? 'unknown',
      });
      this.updateState({
        isConnecting: false,
        error: await TwilioVideoProvider.classifyCaptureError(err, requested),
      });
      throw err;
    }
  }

  /** Which devices a connect config actually asks the browser for. */
  private static requestedKinds(config?: VideoProviderConfig): MediaKind[] {
    const kinds: MediaKind[] = [];
    if (config?.video !== false) kinds.push('camera');
    if (config?.audio !== false) kinds.push('microphone');
    return kinds;
  }

  private static captureOptions(config?: VideoProviderConfig) {
    return {
      audio: config?.audio === false
        ? false
        : config?.audioConstraints
          ? config.audioConstraints
          : true,
      video: config?.video === false
        ? false
        : config?.videoConstraints
          ? config.videoConstraints
          : true,
    };
  }

  /**
   * Start a capture, or join the one already running.
   *
   * At most one `getUserMedia` may be outstanding at a time. The browser shows
   * one dialog per request and answers it once; a second request issued while
   * the first is unanswered leaves an orphan that nothing will ever collect. An
   * identical request therefore joins the pending one — pressing "Try again"
   * while the dialog is open is a no-op rather than another dialog — and a
   * request with different constraints (a different camera picked in the error
   * modal) queues behind it instead of running alongside.
   */
  private acquireLocalTracks(config?: VideoProviderConfig): CaptureAttempt {
    const options = TwilioVideoProvider.captureOptions(config);
    const key = JSON.stringify(options);
    const inFlight = this.capture;

    if (inFlight && inFlight.key === key) {
      inFlight.waiters += 1;
      return inFlight;
    }

    let markStarted: () => void = () => undefined;
    const started = new Promise<void>((resolve) => { markStarted = resolve; });

    const promise: Promise<LocalTracks> = inFlight
      ? inFlight.promise
          .then(() => undefined, () => undefined)
          .then(() => {
            const tracks = Video.createLocalTracks(options);
            markStarted();
            return tracks;
          })
      : (() => {
          const tracks = Video.createLocalTracks(options);
          markStarted();
          return tracks;
        })();

    const attempt: CaptureAttempt = { key, promise, waiters: 1, claimed: false, started };
    this.capture = attempt;
    const clear = () => {
      if (this.capture === attempt) this.capture = null;
    };
    promise.then(clear, clear);
    return attempt;
  }

  /**
   * Map a capture/connect rejection onto a MediaError.
   *
   * The rejection is inspected structurally (`name`) rather than through
   * `instanceof Error`: getUserMedia rejects with a DOMException and
   * OverconstrainedError is its own interface, neither of which is guaranteed to
   * sit on Error.prototype across engines. Falling through to the `network`
   * default there would be actively harmful — it hands a dead camera to the
   * reconnect loop instead of to the device-error prompt.
   *
   * Each distinct cause keeps its own type, because the recovery differs: a
   * site-level block is fixed in browser settings, a busy device by picking
   * another one, and a missing device only by dropping video.
   */
  private static classifyError(err: unknown, requested: MediaRequest | undefined): MediaError {
    const error = TwilioVideoProvider.describeError(err);
    // Only a capture failure has a device to name; a network or security failure
    // was not a question about anyone's camera.
    return error.isLocalCapture ? { ...error, requested } : error;
  }

  /**
   * `classifyError`, plus the one question the rejection cannot answer by itself:
   * was the dialog closed, or is access blocked?
   *
   * Chrome says so in the message ("Permission dismissed"), and that is enough
   * there. Other engines word every refusal the same, so a `permission-denied`
   * is checked against the permission as it reads now: still `prompt` means the
   * browser is ready to ask again, which is what "closed the dialog" means to the
   * customer. Only `denied` keeps the blocked wording — telling someone who
   * merely closed a dialog to go digging in browser settings is the bug.
   */
  private static async classifyCaptureError(
    err: unknown,
    requested: MediaRequest | undefined
  ): Promise<MediaError> {
    const error = TwilioVideoProvider.classifyError(err, requested);
    const isRefusal = error.type === 'permission-denied' || error.type === 'permission-dismissed';
    if (!isRefusal || error.deniedBySystem || !requested) return error;
    const state = await queryCapturePermission(kindsForRequest(requested));
    // Chrome's third closed dialog still says "dismissed", but the block is already
    // in place: what the browser reads now wins over the wording.
    if (state === 'denied') {
      return {
        ...error,
        type: 'permission-denied',
        message: 'Camera/microphone access was denied. Please check your browser settings.',
      };
    }
    return state === 'prompt'
      ? { ...error, type: 'permission-dismissed', message: TwilioVideoProvider.DISMISSED_MESSAGE }
      : error;
  }

  private static readonly DISMISSED_MESSAGE =
    'The camera/microphone request was closed without an answer.';

  private static describeError(err: unknown): MediaError {
    const candidate = err as { name?: unknown; message?: unknown } | null | undefined;
    const errorName = typeof candidate?.name === 'string' ? candidate.name : '';
    const errorMessage =
      typeof candidate?.message === 'string' && candidate.message.length > 0
        ? candidate.message
        : 'Connection failed';

    switch (errorName) {
      // The customer (or a policy) refused access. A different device changes nothing.
      case 'NotAllowedError':
      case 'PermissionDeniedError': // legacy alias, still emitted by older WebKit
        // The dialog was closed, not answered. Nothing is blocked yet: the next
        // request asks again (until Chrome's third dismissal turns it into a block).
        if (isDismissalMessage(candidate?.message)) {
          return {
            type: 'permission-dismissed',
            message: TwilioVideoProvider.DISMISSED_MESSAGE,
            retryable: false,
            isLocalCapture: true,
          };
        }
        return {
          type: 'permission-denied',
          message: 'Camera/microphone access was denied. Please check your browser settings.',
          retryable: false,
          isLocalCapture: true,
          ...(isSystemDenialMessage(candidate?.message) ? { deniedBySystem: true } : {}),
        };

      // Nothing to capture from — the honest recovery is audio-only / chat.
      case 'NotFoundError':
      case 'DevicesNotFoundError': // legacy alias
        return {
          type: 'device-not-found',
          message: 'No camera or microphone found on this device.',
          retryable: false,
          isLocalCapture: true,
        };

      // Device exists but the OS would not hand it over: another app holds it, or a
      // virtual camera (OBS) failed to start. Chrome reports NotReadableError,
      // Firefox AbortError, older Chrome TrackStartError — same cause, same fix.
      case 'NotReadableError':
      case 'TrackStartError':
      case 'AbortError':
        // retryable: true is preserved for other consumers that depend on this value
        return {
          type: 'device-in-use',
          message: 'Camera or microphone is in use by another application.',
          retryable: true,
          isLocalCapture: true,
        };

      case 'OverconstrainedError':
      case 'ConstraintNotSatisfiedError': // legacy alias
        // retryable: true is preserved for other consumers that depend on this value
        return {
          type: 'overconstrained',
          message: 'Camera does not support the requested settings.',
          retryable: true,
          isLocalCapture: true,
        };

      case 'CaptureTimeoutError':
        return {
          type: 'capture-timeout',
          message: errorMessage,
          retryable: false,
          isLocalCapture: true,
        };

      case 'SecurityError':
        return {
          type: 'security',
          message: 'Camera access requires a secure (HTTPS) connection.',
          retryable: false,
        };

      default:
        // A thrown non-error (no usable name) tells us nothing about the cause.
        if (!errorName) {
          return {
            type: 'unknown',
            message: 'Connection failed',
            retryable: true,
          };
        }
        return {
          type: 'network',
          message: errorMessage,
          retryable: true,
        };
    }
  }

  disconnect(): void {
    // Stop local tracks
    this.localTracks.forEach(track => {
      if ('stop' in track && typeof track.stop === 'function') {
        track.stop();
      }
    });
    this.localTracks = [];

    // Detach diagnostic logger before disconnecting
    this.diagDetach?.();
    this.diagDetach = null;

    // Disconnect room
    if (this.room) {
      this.room.disconnect();
      this.room = null;
    }

    this.updateState({
      isConnected: false,
      localParticipant: null,
      remoteParticipants: [],
      localNetworkQuality: 0,
      remoteNetworkQuality: 0,
    });
  }

  async toggleCamera(): Promise<void> {
    const videoTrack = this.localTracks.find(t => t.kind === 'video') as LocalVideoTrack | undefined;
    if (videoTrack) {
      if (videoTrack.isEnabled) {
        videoTrack.disable();
      } else {
        videoTrack.enable();
      }
      this.updateState({ localParticipant: this.buildLocalParticipant() });
      return;
    }

    // No video track exists (e.g. connected with { video: false }) — create and publish one
    // so the user can actually enable the camera mid-call.
    if (!this.room) return;
    if (this.acquiring.camera) return this.acquiring.camera;
    const room = this.room;
    const pending = this.createTrackOrReport('camera', () => Video.createLocalVideoTrack())
      .then((newTrack) => this.publishAcquired(room, newTrack, 'toggleCamera'))
      .finally(() => { delete this.acquiring.camera; });
    this.acquiring.camera = pending;
    return pending;
  }

  private async publishAcquired(
    room: Room,
    newTrack: LocalVideoTrack | LocalAudioTrack,
    caller: string
  ): Promise<void> {
    try {
      this.localTracks.push(newTrack);
      await room.localParticipant.publishTrack(newTrack);
      this.updateState({ localParticipant: this.buildLocalParticipant() });
    } catch (err) {
      console.warn(`${caller}: failed to publish track`, TwilioVideoProvider.errorFields(err));
    }
  }

  async toggleMic(): Promise<void> {
    const audioTrack = this.localTracks.find(t => t.kind === 'audio') as LocalAudioTrack | undefined;
    if (audioTrack) {
      if (audioTrack.isEnabled) {
        audioTrack.disable();
      } else {
        audioTrack.enable();
      }
      this.updateState({ localParticipant: this.buildLocalParticipant() });
      return;
    }

    if (!this.room) return;
    if (this.acquiring.microphone) return this.acquiring.microphone;
    const room = this.room;
    const pending = this.createTrackOrReport('microphone', () => Video.createLocalAudioTrack())
      .then((newTrack) => this.publishAcquired(room, newTrack, 'toggleMic'))
      .finally(() => { delete this.acquiring.microphone; });
    this.acquiring.microphone = pending;
    return pending;
  }

  /**
   * Acquire a track for a device switched on inside a room that already exists.
   *
   * This is the camera of a customer who joined by voice and moved to video: the
   * browser asks about the camera only now. A refusal here used to end in
   * `console.warn` — no modal, a "video" label and no camera. It is reported the
   * same way a failed connect is, through `state.error`, and rethrown so the
   * caller can put the mode back. The call to `create` stays synchronous, so a
   * click that got here still reaches `getUserMedia` inside its gesture.
   */
  private async createTrackOrReport<T extends LocalVideoTrack | LocalAudioTrack>(
    kind: MediaKind,
    create: () => Promise<T>
  ): Promise<T> {
    try {
      const track = await create();
      if (this.state.error?.isLocalCapture) this.updateState({ error: null });
      return track;
    } catch (err) {
      console.warn(`toggle ${kind}: failed to acquire track`, TwilioVideoProvider.errorFields(err));
      this.updateState({ error: await TwilioVideoProvider.classifyCaptureError(err, kind) });
      throw err;
    }
  }

  /** A rejection as loggable fields: a DOMException serialises to `{}`. */
  private static errorFields(err: unknown): { name: unknown; message: unknown } {
    const candidate = err as { name?: unknown; message?: unknown } | null | undefined;
    return { name: candidate?.name, message: candidate?.message };
  }

  getLocalVideoTrack(): VideoTrack | null {
    const track = this.localTracks.find(t => t.kind === 'video') as LocalVideoTrack;
    return track ? this.wrapTrack(track) : null;
  }

  getRemoteVideoTrack(participantId?: string): VideoTrack | null {
    if (!this.room) return null;

    const participant = participantId
      ? this.room.participants.get(participantId)
      : this.room.participants.values().next().value;

    if (!participant) return null;

    let videoTrack: RemoteTrack | null = null;
    participant.videoTracks.forEach(pub => {
      if (pub.track) videoTrack = pub.track;
    });

    return videoTrack ? this.wrapTrack(videoTrack) : null;
  }

  private setupRemoteNQListener(participant: RemoteParticipant): void {
    participant.on('networkQualityLevelChanged', (level: number) => {
      this.updateState({ remoteNetworkQuality: level });
    });
  }

  private attachRemoteParticipantListeners(participant: RemoteParticipant): void {
    participant.on('trackSubscribed', () => {
      this.updateRemoteParticipant(participant);
    });
    participant.on('trackUnsubscribed', () => {
      this.updateRemoteParticipant(participant);
    });
    this.setupRemoteNQListener(participant);
  }

  private buildLocalParticipant(): VideoParticipant | null {
    if (!this.room) return null;

    const videoTrack = this.localTracks.find(t => t.kind === 'video');
    const audioTrack = this.localTracks.find(t => t.kind === 'audio');

    return {
      id: this.room.localParticipant.sid,
      identity: this.room.localParticipant.identity,
      videoTrack: videoTrack ? this.wrapTrack(videoTrack) : null,
      audioTrack: audioTrack ? this.wrapTrack(audioTrack) : null,
    };
  }

  private buildRemoteParticipant(participant: RemoteParticipant): VideoParticipant {
    let videoTrack: RemoteTrack | null = null;
    let audioTrack: RemoteTrack | null = null;

    participant.videoTracks.forEach(pub => {
      if (pub.track) videoTrack = pub.track;
    });
    participant.audioTracks.forEach(pub => {
      if (pub.track) audioTrack = pub.track;
    });

    return {
      id: participant.sid,
      identity: participant.identity,
      videoTrack: videoTrack ? this.wrapTrack(videoTrack) : null,
      audioTrack: audioTrack ? this.wrapTrack(audioTrack) : null,
    };
  }

  private updateRemoteParticipant(participant: RemoteParticipant): void {
    const updated = this.buildRemoteParticipant(participant);
    this.updateState({
      remoteParticipants: this.state.remoteParticipants.map(p =>
        p.id === updated.id ? updated : p
      ),
    });
  }

  async switchDevice(kind: 'video' | 'audio', deviceId: string): Promise<void> {
    if (!this.room) return;

    const twilioKind = kind === 'video' ? 'video' : 'audio';
    const constraint = kind === 'video'
      ? { video: { deviceId: { exact: deviceId } } as MediaTrackConstraints, audio: false }
      : { audio: { deviceId: { exact: deviceId } } as MediaTrackConstraints, video: false };

    // Create new track first (before touching old track — abort cleanly on failure)
    let newTrack: LocalVideoTrack | LocalAudioTrack;
    try {
      const tracks = await Video.createLocalTracks(constraint);
      newTrack = tracks[0] as LocalVideoTrack | LocalAudioTrack;
    } catch (createError) {
      console.error('[TwilioVideoProvider] switchDevice: failed to create new track', createError);
      throw createError;
    }

    // Find old track of the same kind
    const oldTrackIndex = this.localTracks.findIndex(t => t.kind === twilioKind);
    const oldTrack = oldTrackIndex !== -1 ? this.localTracks[oldTrackIndex] : null;

    try {
      // Unpublish old track (if present) before publishing new one
      if (oldTrack) {
        this.room.localParticipant.unpublishTrack(oldTrack as LocalVideoTrack | LocalAudioTrack);
      }

      // Publish new track
      await this.room.localParticipant.publishTrack(newTrack);

      // Stop old track only after publish succeeds
      if (oldTrack && 'stop' in oldTrack && typeof (oldTrack as { stop?: () => void }).stop === 'function') {
        (oldTrack as { stop: () => void }).stop();
      }

      // Update local tracks array
      if (oldTrackIndex !== -1) {
        this.localTracks[oldTrackIndex] = newTrack;
      } else {
        this.localTracks.push(newTrack);
      }
    } catch (publishError) {
      // Publish failed -- stop the new track and try to restore old track
      console.error('[TwilioVideoProvider] switchDevice: failed to publish new track, restoring old track', publishError);
      newTrack.stop();
      if (oldTrack) {
        try {
          await this.room.localParticipant.publishTrack(oldTrack as LocalVideoTrack | LocalAudioTrack);
        } catch (restoreError) {
          console.error('[TwilioVideoProvider] switchDevice: failed to restore old track', restoreError);
        }
      }
      throw publishError;
    }

    // Update state to reflect new track
    this.updateState({
      localParticipant: this.buildLocalParticipant(),
    });
  }

  private wrapTrack(track: LocalTrack | RemoteTrack): VideoTrack {
    const trackId = 'sid' in track ? track.sid : String(Date.now());

    // Type assertion needed: Twilio SDK types don't expose attach/detach methods in union types
    // but they exist at runtime on both LocalTrack and RemoteTrack instances
    type TwilioTrackWithMethods = {
      attach: (el: HTMLMediaElement) => HTMLMediaElement;
      detach: () => HTMLMediaElement[];
      stop?: () => void;
      enable?: () => void;
      disable?: () => void;
    };

    const trackWithMethods = track as unknown as TwilioTrackWithMethods;

    return {
      id: trackId,
      kind: track.kind as 'video' | 'audio',
      enabled: 'isEnabled' in track ? track.isEnabled : true,
      attach: (el) => trackWithMethods.attach(el),
      detach: () => trackWithMethods.detach(),
      stop: () => track.kind === 'video' || track.kind === 'audio' ? trackWithMethods.stop?.() : undefined,
      enable: () => trackWithMethods.enable?.(),
      disable: () => trackWithMethods.disable?.(),
    };
  }
}
